import { Button, Dropdown, Menu } from 'antd';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useCookies } from 'react-cookie';
import { ButtonType } from 'antd/lib/button';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CountryCode,
  deleteCookie,
  envCookieName,
  getCookie,
  getDomainName,
  isUndefinedOrNullOrEmpty,
  setCookie,
} from 'common';
import { changeCountryCode, employerSelect, RootState, switchEmployerAdminAuth } from 'modules';
import { DownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getEmployerInfo } from 'hooks';

export interface EmployerListDropdownProp {
  type?: ButtonType;
  btnClassName?: string;
}

export const EmployerListDropdown: React.FC<EmployerListDropdownProp> = ({ btnClassName, type }) => {
  const dispatch = useDispatch();
  const [, setCustomCookie, deleteCustomCookie] = useCookies();
  const [, setRefreshCookie] = useCookies([envCookieName('refreshToken')]);
  const [, setAccessTokenCookie, deleteAccessTokenCookie] = useCookies(['accessToken']);
  const [, setExpiredOnCookie] = useCookies([envCookieName('expiredOn')]);
  const [, setEmployerIdCookie] = useCookies([envCookieName('employerId')]);
  const { employers } = useSelector((state: RootState) => state.user);
  const [updatedEid, setUpdatedEid] = useState<string>(getCookie(envCookieName('employerId')));

  const switchEmployer = useCallback(
    (values: { eid: string; name: string; country: CountryCode }) => {
      if (updatedEid !== values.eid) {
        dispatch(
          switchEmployerAdminAuth(
            { eid: values.eid, countryCode: values.country },
            async (data: { refreshToken: string; expiredOn: number; accessToken: string; permissions: unknown }) => {
              dispatch(employerSelect());
              deleteCookie('refreshToken');
              setRefreshCookie(envCookieName('refreshToken'), data.refreshToken, {
                domain: getDomainName(),
                expires: dayjs.unix(data.expiredOn).toDate(),
              });
              // Clean up any leftover chunk cookies from previous sessions
              deleteAccessTokenCookie('accessToken');
              const existingChunks = parseInt(getCookie('accessTokenChunks') || '0', 10);
              for (let i = 0; i < existingChunks; i += 1) {
                deleteCustomCookie(`accessTokenChunk${i}`);
              }
              deleteCustomCookie('accessTokenChunks');
              if (data.accessToken.length <= 4096) {
                setAccessTokenCookie('accessToken', data.accessToken, {
                  domain: getDomainName(),
                  expires: dayjs.unix(data.expiredOn).toDate(),
                });
              } else {
                // Split access token into chunks
                const CHUNK_SIZE = 2048;
                const tokenChunks: string[] = [];
                for (let i = 0; i < data.accessToken.length; i += CHUNK_SIZE) {
                  tokenChunks.push(data.accessToken.slice(i, i + CHUNK_SIZE));
                }
                setCustomCookie('accessTokenChunks', tokenChunks.length.toString(), {
                  domain: getDomainName(),
                  expires: dayjs.unix(data.expiredOn).toDate(),
                });
                tokenChunks.forEach((chunk, index) => {
                  setCustomCookie(`accessTokenChunk${index}`, chunk, {
                    domain: getDomainName(),
                    expires: dayjs.unix(data.expiredOn).toDate(),
                  });
                });
              }
              deleteCookie('expiredOn');
              setExpiredOnCookie(envCookieName('expiredOn'), data.expiredOn, {
                domain: getDomainName(),
                expires: dayjs.unix(data.expiredOn).toDate(),
              });
              deleteCookie('employerId');
              setEmployerIdCookie(envCookieName('employerId'), values.eid, {
                domain: getDomainName(),
              });
              setCookie('permissions', JSON.stringify(data.permissions || []));
              setUpdatedEid(values.eid);

              // Get employer info for bulk import approval flag
              const employerInfoResp = await getEmployerInfo(values.eid);
              const { data: employerInfo } = employerInfoResp;
              setCookie('bulkImportApproval', employerInfo?.setting?.bulkImportApprovalFlag || false);
            }
          )
        );
      }
    },
    [dispatch, updatedEid, setAccessTokenCookie, setEmployerIdCookie, setExpiredOnCookie, setRefreshCookie]
  );

  // Pure: just the display name. Dispatching an action inside useMemo is a side-effect that re-fires
  // on every render and RE-SETS the redux country to the displayed employer's country — which
  // clobbered a fresh CountryDropdown selection (switch to ID, but this re-pins it to the previously
  // impersonated employer's country, so the next impersonate request sent the OLD country → the
  // cross-country impersonation "unauthorized" bug). Keep this pure.
  const getCompanyName = useMemo(() => {
    const result = employers.find((item) => item.eid === updatedEid);
    return result?.name ?? '-';
  }, [employers, updatedEid]);

  // Sync the active country to the SELECTED employer — but only when that employer actually changes
  // (not on every render), so it reflects the current selection instead of continually overriding it.
  const selectedEmployerCountry = useMemo(
    () => employers.find((item) => item.eid === updatedEid)?.country,
    [employers, updatedEid]
  );
  useEffect(() => {
    if (selectedEmployerCountry) {
      dispatch(changeCountryCode({ code: selectedEmployerCountry }));
    }
  }, [selectedEmployerCountry, dispatch]);

  const employerListMenu = (
    <Menu selectedKeys={[updatedEid]}>
      {employers.map((item) => (
        <Menu.Item
          key={item.eid}
          onClick={() => switchEmployer({ eid: item.eid, name: item.name, country: item.country })}
        >
          {item.name}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Dropdown trigger={['click']} overlay={employerListMenu}>
      <Button className={btnClassName} type={type}>
        {getCompanyName} <DownOutlined />
      </Button>
    </Dropdown>
  );
};
